import { useState, useMemo, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus,
  ChevronDown,
  ChevronRight,
  Pencil,
  Trash2,
  GripVertical,
  Package,
  FileText,
  Layers,
  Upload,
} from "lucide-react";
import {
  releaseNoteSectionsApi,
  sectionTypesApi,
  ReleaseNoteSection,
  ReleaseNoteSectionType,
  ReleaseNoteItem,
  ItemSchemaField,
} from "@/api/client";
import DynamicItemForm, { BulkAddForm } from "./DynamicItemForm";
import ConfirmationModal from "./ConfirmationModal";

interface ReleaseNotesTabProps {
  deploymentId: string;
  canEdit: boolean;
}

export default function ReleaseNotesTab({
  deploymentId,
  canEdit,
}: ReleaseNotesTabProps) {
  const queryClient = useQueryClient();
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );
  const [showAddSection, setShowAddSection] = useState(false);
  const [editingSection, setEditingSection] =
    useState<ReleaseNoteSection | null>(null);
  const [addingItemToSection, setAddingItemToSection] = useState<string | null>(
    null,
  );
  const [bulkAddingToSection, setBulkAddingToSection] = useState<string | null>(
    null,
  );
  const [editingItem, setEditingItem] = useState<{
    sectionId: string;
    item: ReleaseNoteItem;
  } | null>(null);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  // Fetch sections with items
  const { data: sectionsData, isLoading: sectionsLoading } = useQuery({
    queryKey: ["deployment-sections", deploymentId],
    queryFn: () => releaseNoteSectionsApi.list(deploymentId, true),
  });

  // Fetch available section types
  const { data: sectionTypesData } = useQuery({
    queryKey: ["section-types"],
    queryFn: () => sectionTypesApi.list(),
  });

  const sections = useMemo(
    () =>
      [...(sectionsData?.sections || [])].sort(
        (a, b) => a.display_order - b.display_order,
      ),
    [sectionsData],
  );
  const sectionTypes = sectionTypesData?.section_types || [];

  // Default all sections to expanded when sections load
  useEffect(() => {
    if (sections.length > 0 && expandedSections.size === 0) {
      setExpandedSections(new Set(sections.map((s) => s.id)));
    }
  }, [sections, expandedSections.size]);

  // Get section types not already used in this deployment
  const availableSectionTypes = sectionTypes.filter(
    (type) => !sections.some((s) => s.section_type_id === type.id),
  );

  // Mutations
  const createSectionMutation = useMutation({
    mutationKey: ["createSection"],
    mutationFn: (data: {
      section_type_id: string;
      title?: string;
      notes?: string;
    }) => releaseNoteSectionsApi.create(deploymentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-sections", deploymentId],
      });
      setShowAddSection(false);
    },
  });

  const updateSectionMutation = useMutation({
    mutationKey: ["updateSection"],
    mutationFn: ({
      sectionId,
      data,
    }: {
      sectionId: string;
      data: { title?: string; notes?: string };
    }) => releaseNoteSectionsApi.update(deploymentId, sectionId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-sections", deploymentId],
      });
      setEditingSection(null);
    },
  });

  const deleteSectionMutation = useMutation({
    mutationKey: ["deleteSection"],
    mutationFn: (sectionId: string) =>
      releaseNoteSectionsApi.delete(deploymentId, sectionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-sections", deploymentId],
      });
    },
  });

  const reorderSectionsMutation = useMutation({
    mutationKey: ["reorderSections"],
    mutationFn: (order: string[]) =>
      releaseNoteSectionsApi.reorder(deploymentId, order),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-sections", deploymentId],
      });
    },
  });

  const createItemMutation = useMutation({
    mutationKey: ["createItem"],
    mutationFn: ({
      sectionId,
      data,
    }: {
      sectionId: string;
      data: Record<string, unknown>;
    }) => releaseNoteSectionsApi.createItem(deploymentId, sectionId, { data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-sections", deploymentId],
      });
      setAddingItemToSection(null);
    },
  });

  const bulkCreateItemsMutation = useMutation({
    mutationKey: ["createItem"],
    mutationFn: ({
      sectionId,
      items,
    }: {
      sectionId: string;
      items: { data: Record<string, unknown> }[];
    }) =>
      releaseNoteSectionsApi.bulkCreateItems(deploymentId, sectionId, items),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-sections", deploymentId],
      });
      setBulkAddingToSection(null);
    },
  });

  const updateItemMutation = useMutation({
    mutationKey: ["updateItem"],
    mutationFn: ({
      sectionId,
      itemId,
      data,
    }: {
      sectionId: string;
      itemId: string;
      data: Record<string, unknown>;
    }) =>
      releaseNoteSectionsApi.updateItem(deploymentId, sectionId, itemId, {
        data,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-sections", deploymentId],
      });
      setEditingItem(null);
    },
  });

  const deleteItemMutation = useMutation({
    mutationKey: ["deleteItem"],
    mutationFn: ({
      sectionId,
      itemId,
    }: {
      sectionId: string;
      itemId: string;
    }) => releaseNoteSectionsApi.deleteItem(deploymentId, sectionId, itemId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-sections", deploymentId],
      });
    },
  });

  const reorderItemsMutation = useMutation({
    mutationKey: ["reorderItems"],
    mutationFn: ({
      sectionId,
      order,
    }: {
      sectionId: string;
      order: string[];
    }) => releaseNoteSectionsApi.reorderItems(deploymentId, sectionId, order),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["deployment-sections", deploymentId],
      });
    },
  });

  const handleSectionDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      const oldIndex = sections.findIndex((s) => s.id === active.id);
      const newIndex = sections.findIndex((s) => s.id === over.id);
      const newOrder = arrayMove(sections, oldIndex, newIndex).map((s) => s.id);
      reorderSectionsMutation.mutate(newOrder);
    }
  };

  const handleItemDragEnd = (sectionId: string, items: ReleaseNoteItem[]) => {
    return (event: DragEndEvent) => {
      const { active, over } = event;

      if (over && active.id !== over.id) {
        const oldIndex = items.findIndex((i) => i.id === active.id);
        const newIndex = items.findIndex((i) => i.id === over.id);
        const newOrder = arrayMove(items, oldIndex, newIndex).map((i) => i.id);
        reorderItemsMutation.mutate({ sectionId, order: newOrder });
      }
    };
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  const getSectionType = (
    sectionTypeId: string,
  ): ReleaseNoteSectionType | undefined => {
    return sectionTypes.find((t) => t.id === sectionTypeId);
  };

  const getItemSchema = (sectionTypeId: string): ItemSchemaField[] => {
    const sectionType = getSectionType(sectionTypeId);
    if (!sectionType?.item_schema) return [];
    try {
      const schema =
        typeof sectionType.item_schema === "string"
          ? JSON.parse(sectionType.item_schema)
          : sectionType.item_schema;
      return schema.fields || [];
    } catch {
      return [];
    }
  };

  const getCategoryIcon = (category: string) => {
    return category === "deployment" ? (
      <Package className="w-4 h-4" />
    ) : (
      <FileText className="w-4 h-4" />
    );
  };

  const closeConfirmModal = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  const showConfirm = (
    title: string,
    message: string,
    onConfirm: () => void,
  ) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm });
  };

  if (sectionsLoading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  // Read-only view for non-editable deployments
  if (!canEdit) {
    return (
      <ReadOnlyReleaseNotes
        sections={sections}
        sectionTypes={sectionTypes}
        getItemSchema={getItemSchema}
      />
    );
  }

  // Editable view
  return (
    <div className="space-y-4">
      {/* Action buttons */}
      <div className="flex justify-end gap-2">
        <button
          onClick={() => setShowAddSection(true)}
          className="btn btn-primary"
          disabled={availableSectionTypes.length === 0}
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Section
        </button>
      </div>

      {/* Add Section Form */}
      {showAddSection && (
        <AddSectionForm
          sectionTypes={availableSectionTypes}
          onSubmit={(data) => createSectionMutation.mutate(data)}
          onCancel={() => setShowAddSection(false)}
          isSubmitting={createSectionMutation.isPending}
        />
      )}

      {/* Sections List */}
      {sections.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            No release notes sections
          </h3>
          <p className="text-gray-600 mb-4">
            Add sections to document containers, config changes, and other
            deployment details.
          </p>
          {availableSectionTypes.length > 0 && (
            <button
              onClick={() => setShowAddSection(true)}
              className="btn btn-primary"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add First Section
            </button>
          )}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleSectionDragEnd}
        >
          <SortableContext
            items={sections.map((s) => s.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-3">
              {sections.map((section) => {
                const sectionType = getSectionType(section.section_type_id);
                const isExpanded = expandedSections.has(section.id);
                const itemSchema = getItemSchema(section.section_type_id);
                const items = [...(section.items || [])].sort(
                  (a, b) => a.display_order - b.display_order,
                );

                return (
                  <SortableSection
                    key={section.id}
                    section={section}
                    sectionType={sectionType}
                    isExpanded={isExpanded}
                    canEdit={true}
                    onToggle={() => toggleSection(section.id)}
                    onEdit={() => setEditingSection(section)}
                    onDelete={() =>
                      showConfirm(
                        "Delete Section",
                        `Delete "${section.title || sectionType?.name}" and all its items?`,
                        () => {
                          deleteSectionMutation.mutate(section.id);
                          closeConfirmModal();
                        },
                      )
                    }
                    getCategoryIcon={getCategoryIcon}
                  >
                    {isExpanded && (
                      <div className="border-t border-gray-200">
                        {/* Edit Section Form */}
                        {editingSection?.id === section.id && (
                          <div className="p-4 bg-gray-50 border-b border-gray-200">
                            <EditSectionForm
                              section={section}
                              onSubmit={(data) =>
                                updateSectionMutation.mutate({
                                  sectionId: section.id,
                                  data,
                                })
                              }
                              onCancel={() => setEditingSection(null)}
                              isSubmitting={updateSectionMutation.isPending}
                            />
                          </div>
                        )}

                        {/* Add/Bulk Add Item Forms */}
                        {addingItemToSection === section.id && (
                          <div className="p-4 bg-blue-50 border-b border-gray-200">
                            <h4 className="font-medium text-gray-900 mb-3">
                              Add Item
                            </h4>
                            <DynamicItemForm
                              fields={itemSchema}
                              onSubmit={(data) =>
                                createItemMutation.mutate({
                                  sectionId: section.id,
                                  data,
                                })
                              }
                              onCancel={() => setAddingItemToSection(null)}
                              isSubmitting={createItemMutation.isPending}
                            />
                          </div>
                        )}

                        {bulkAddingToSection === section.id && (
                          <div className="p-4 bg-blue-50 border-b border-gray-200">
                            <h4 className="font-medium text-gray-900 mb-3">
                              Bulk Add Items
                            </h4>
                            <BulkAddForm
                              fields={itemSchema}
                              onSubmit={(bulkItems) =>
                                bulkCreateItemsMutation.mutate({
                                  sectionId: section.id,
                                  items: bulkItems.map((data) => ({ data })),
                                })
                              }
                              onCancel={() => setBulkAddingToSection(null)}
                              isSubmitting={bulkCreateItemsMutation.isPending}
                            />
                          </div>
                        )}

                        {/* Edit Item Form */}
                        {editingItem?.sectionId === section.id && (
                          <div className="p-4 bg-yellow-50 border-b border-gray-200">
                            <h4 className="font-medium text-gray-900 mb-3">
                              Edit Item
                            </h4>
                            <DynamicItemForm
                              fields={itemSchema}
                              initialData={
                                editingItem.item.data as Record<string, unknown>
                              }
                              onSubmit={(data) =>
                                updateItemMutation.mutate({
                                  sectionId: section.id,
                                  itemId: editingItem.item.id,
                                  data,
                                })
                              }
                              onCancel={() => setEditingItem(null)}
                              isSubmitting={updateItemMutation.isPending}
                              submitLabel="Update"
                            />
                          </div>
                        )}

                        {/* Items Table */}
                        {items.length === 0 ? (
                          <div className="p-8 text-center text-gray-500">
                            <p className="mb-4">
                              No items in this section yet.
                            </p>
                            <div className="flex justify-center gap-2">
                              <button
                                onClick={() =>
                                  setAddingItemToSection(section.id)
                                }
                                className="btn btn-secondary"
                              >
                                <Plus className="w-4 h-4 mr-2" />
                                Add Item
                              </button>
                              <button
                                onClick={() =>
                                  setBulkAddingToSection(section.id)
                                }
                                className="btn btn-secondary"
                              >
                                <Upload className="w-4 h-4 mr-2" />
                                Bulk Add
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <DndContext
                              sensors={sensors}
                              collisionDetection={closestCenter}
                              onDragEnd={handleItemDragEnd(section.id, items)}
                            >
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-gray-50">
                                    <tr>
                                      <th className="w-8 px-3 py-2"></th>
                                      {itemSchema.map((field) => (
                                        <th
                                          key={field.name}
                                          className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase"
                                        >
                                          {field.label}
                                        </th>
                                      ))}
                                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                        Actions
                                      </th>
                                    </tr>
                                  </thead>
                                  <SortableContext
                                    items={items.map((i) => i.id)}
                                    strategy={verticalListSortingStrategy}
                                  >
                                    <tbody className="divide-y divide-gray-200">
                                      {items.map((item) => (
                                        <SortableItemRow
                                          key={item.id}
                                          item={item}
                                          itemSchema={itemSchema}
                                          canEdit={true}
                                          onEdit={() =>
                                            setEditingItem({
                                              sectionId: section.id,
                                              item,
                                            })
                                          }
                                          onDelete={() =>
                                            showConfirm(
                                              "Delete Item",
                                              "Are you sure you want to delete this item?",
                                              () => {
                                                deleteItemMutation.mutate({
                                                  sectionId: section.id,
                                                  itemId: item.id,
                                                });
                                                closeConfirmModal();
                                              },
                                            )
                                          }
                                        />
                                      ))}
                                    </tbody>
                                  </SortableContext>
                                </table>
                              </div>
                            </DndContext>
                            <div className="px-4 py-3 bg-gray-50 border-t border-gray-200 flex gap-2">
                              <button
                                onClick={() =>
                                  setAddingItemToSection(section.id)
                                }
                                className="btn btn-secondary btn-sm"
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Add Item
                              </button>
                              <button
                                onClick={() =>
                                  setBulkAddingToSection(section.id)
                                }
                                className="btn btn-secondary btn-sm"
                              >
                                <Upload className="w-4 h-4 mr-1" />
                                Bulk Add
                              </button>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </SortableSection>
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      )}

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={closeConfirmModal}
        onConfirm={confirmModal.onConfirm}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmLabel="Delete"
        variant="danger"
        isLoading={
          deleteSectionMutation.isPending || deleteItemMutation.isPending
        }
      />
    </div>
  );
}

// Read-only view component for non-draft deployments
interface ReadOnlyReleaseNotesProps {
  sections: ReleaseNoteSection[];
  sectionTypes: ReleaseNoteSectionType[];
  getItemSchema: (sectionTypeId: string) => ItemSchemaField[];
}

function ReadOnlyReleaseNotes({
  sections,
  sectionTypes,
  getItemSchema,
}: ReadOnlyReleaseNotesProps) {
  const getSectionType = (sectionTypeId: string) =>
    sectionTypes.find((t) => t.id === sectionTypeId);

  if (sections.length === 0) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <Layers className="w-12 h-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          No release notes
        </h3>
        <p className="text-gray-500">
          No release notes were added to this deployment.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {sections.map((section) => {
        const sectionType = getSectionType(section.section_type_id);
        const itemSchema = getItemSchema(section.section_type_id);
        const items = [...(section.items || [])].sort(
          (a, b) => a.display_order - b.display_order,
        );

        return (
          <div
            key={section.id}
            className="bg-white rounded-lg border border-gray-200 overflow-hidden"
          >
            {/* Section Header */}
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div
                  className={`p-1.5 rounded ${
                    sectionType?.category === "deployment"
                      ? "bg-blue-100 text-blue-600"
                      : "bg-purple-100 text-purple-600"
                  }`}
                >
                  {sectionType?.category === "deployment" ? (
                    <Package className="w-4 h-4" />
                  ) : (
                    <FileText className="w-4 h-4" />
                  )}
                </div>
                <div>
                  <h3 className="font-medium text-gray-900">
                    {section.title || sectionType?.name || "Section"}
                  </h3>
                  {section.notes && (
                    <p className="text-sm text-gray-500 whitespace-pre-wrap">
                      {section.notes}
                    </p>
                  )}
                </div>
              </div>
            </div>

            {/* Items Table */}
            {items.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No items in this section.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {itemSchema.map((field) => (
                        <th
                          key={field.name}
                          className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                          {field.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {items.map((item) => {
                      const itemData = item.data as Record<string, unknown>;
                      return (
                        <tr key={item.id}>
                          {itemSchema.map((field) => (
                            <td
                              key={field.name}
                              className="px-4 py-3 text-sm text-gray-900"
                            >
                              {renderItemValue(
                                itemData[field.name],
                                field.type,
                              )}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// Sortable Section Component
interface SortableSectionProps {
  section: ReleaseNoteSection;
  sectionType: ReleaseNoteSectionType | undefined;
  isExpanded: boolean;
  canEdit: boolean;
  onToggle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  getCategoryIcon: (category: string) => React.ReactNode;
  children?: React.ReactNode;
}

function SortableSection({
  section,
  sectionType,
  isExpanded,
  canEdit,
  onToggle,
  onEdit,
  onDelete,
  getCategoryIcon,
  children,
}: SortableSectionProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const items = section.items || [];

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-lg border border-gray-200 overflow-hidden"
    >
      {/* Section Header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-gray-50">
        {canEdit && (
          <button
            {...attributes}
            {...listeners}
            className="touch-none text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        )}
        <button onClick={onToggle} className="text-gray-500">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5" />
          ) : (
            <ChevronRight className="w-5 h-5" />
          )}
        </button>
        <div
          className={`p-1.5 rounded ${
            sectionType?.category === "deployment"
              ? "bg-blue-100 text-blue-600"
              : "bg-purple-100 text-purple-600"
          }`}
        >
          {getCategoryIcon(sectionType?.category || "deployment")}
        </div>
        <div className="flex-1 cursor-pointer" onClick={onToggle}>
          <h3 className="font-medium text-gray-900">
            {section.title || sectionType?.name || "Section"}
          </h3>
          {section.notes && (
            <p className="text-sm text-gray-500 truncate">{section.notes}</p>
          )}
        </div>
        <span className="text-sm text-gray-500 bg-gray-200 px-2 py-0.5 rounded-full">
          {items.length} {items.length === 1 ? "item" : "items"}
        </span>
        {canEdit && (
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              className="p-1.5 text-gray-400 hover:text-gray-600 rounded"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 text-gray-400 hover:text-red-600 rounded"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>

      {/* Section Content */}
      {children}
    </div>
  );
}

// Sortable Item Row Component
interface SortableItemRowProps {
  item: ReleaseNoteItem;
  itemSchema: ItemSchemaField[];
  canEdit: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

function SortableItemRow({
  item,
  itemSchema,
  canEdit,
  onEdit,
  onDelete,
}: SortableItemRowProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !canEdit });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const itemData = item.data as Record<string, unknown>;

  return (
    <tr ref={setNodeRef} style={style} className="hover:bg-gray-50">
      {canEdit && (
        <td className="px-3 py-3 text-gray-400">
          <button
            {...attributes}
            {...listeners}
            className="touch-none cursor-grab active:cursor-grabbing"
          >
            <GripVertical className="w-4 h-4" />
          </button>
        </td>
      )}
      {itemSchema.map((field) => (
        <td key={field.name} className="px-4 py-3 text-sm text-gray-900">
          {renderItemValue(itemData[field.name], field.type)}
        </td>
      ))}
      {canEdit && (
        <td className="px-4 py-3 text-right">
          <div className="flex justify-end gap-1">
            <button
              onClick={onEdit}
              className="p-1.5 text-gray-400 hover:text-gray-600"
            >
              <Pencil className="w-4 h-4" />
            </button>
            <button
              onClick={onDelete}
              className="p-1.5 text-gray-400 hover:text-red-600"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}

// Helper to render item values based on field type
function renderItemValue(value: unknown, fieldType: string): React.ReactNode {
  if (value === null || value === undefined || value === "") {
    return <span className="text-gray-400">-</span>;
  }

  if (fieldType === "url" && typeof value === "string") {
    return (
      <a
        href={value}
        target="_blank"
        rel="noopener noreferrer"
        className="text-brand-600 hover:text-brand-700 hover:underline truncate max-w-xs inline-block"
      >
        {value}
      </a>
    );
  }

  if (fieldType === "checkbox") {
    return value ? "Yes" : "No";
  }

  // Preserve whitespace/newlines for text content
  return <span className="whitespace-pre-wrap">{String(value)}</span>;
}

// Add Section Form Component
interface AddSectionFormProps {
  sectionTypes: ReleaseNoteSectionType[];
  onSubmit: (data: {
    section_type_id: string;
    title?: string;
    notes?: string;
  }) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

function AddSectionForm({
  sectionTypes,
  onSubmit,
  onCancel,
  isSubmitting,
}: AddSectionFormProps) {
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");

  const selectedType = sectionTypes.find((t) => t.id === selectedTypeId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTypeId) return;
    onSubmit({
      section_type_id: selectedTypeId,
      title: title || undefined,
      notes: notes || undefined,
    });
  };

  // Group section types by category
  const deploymentTypes = sectionTypes.filter(
    (t) => t.category === "deployment",
  );
  const informationalTypes = sectionTypes.filter(
    (t) => t.category === "informational",
  );

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Add Section</h3>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Section Type</label>
          <select
            value={selectedTypeId}
            onChange={(e) => setSelectedTypeId(e.target.value)}
            className="input w-full"
            required
          >
            <option value="">Select a section type...</option>
            {deploymentTypes.length > 0 && (
              <optgroup label="Deployment Sections">
                {deploymentTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </optgroup>
            )}
            {informationalTypes.length > 0 && (
              <optgroup label="Informational Sections">
                {informationalTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </optgroup>
            )}
          </select>
          {selectedType?.description && (
            <p className="mt-1 text-sm text-gray-500 whitespace-pre-wrap">
              {selectedType.description}
            </p>
          )}
        </div>

        <div>
          <label className="label">
            Title Override <span className="text-gray-400">(optional)</span>
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input w-full"
            placeholder={selectedType?.name || "Use default title"}
          />
        </div>

        <div>
          <label className="label">
            Notes <span className="text-gray-400">(optional)</span>
          </label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input w-full"
            rows={2}
            placeholder="Section-level instructions or notes..."
          />
        </div>

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isSubmitting || !selectedTypeId}
          >
            {isSubmitting ? "Adding..." : "Add Section"}
          </button>
        </div>
      </form>
    </div>
  );
}

// Edit Section Form Component
interface EditSectionFormProps {
  section: ReleaseNoteSection;
  onSubmit: (data: { title?: string; notes?: string }) => void;
  onCancel: () => void;
  isSubmitting: boolean;
}

function EditSectionForm({
  section,
  onSubmit,
  onCancel,
  isSubmitting,
}: EditSectionFormProps) {
  const [title, setTitle] = useState(section.title || "");
  const [notes, setNotes] = useState(section.notes || "");

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      title: title || undefined,
      notes: notes || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="label">Title Override</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="input w-full"
            placeholder="Use default title"
          />
        </div>
        <div>
          <label className="label">Notes</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="input w-full"
            placeholder="Section notes..."
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting}
        >
          {isSubmitting ? "Saving..." : "Save"}
        </button>
      </div>
    </form>
  );
}
